import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { InlineWidget } from 'react-calendly';
import { CALENDLY_URL } from '../booking';

const BookingContext = createContext(undefined);

export function BookingProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const openCalendly = useCallback(() => setIsOpen(true), []);
  const onModalClose = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onModalClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onModalClose]);

  const modal =
    isOpen &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        className="booking-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Schedule a call"
      >
        <button
          type="button"
          className="booking-modal-backdrop"
          onClick={onModalClose}
          tabIndex={-1}
          aria-label="Close scheduling"
        />
        <div className="booking-modal-panel">
          <div className="booking-modal-header">
            <button type="button" className="booking-modal-close" onClick={onModalClose} aria-label="Close">
              ×
            </button>
          </div>
          <div className="booking-modal-widget">
            <InlineWidget
              url={CALENDLY_URL}
              styles={{ width: '100%', height: '100%', minWidth: 0 }}
              iframeTitle="Schedule with Cure8 Studios"
            />
          </div>
        </div>
      </div>,
      document.body
    );

  const value = useMemo(() => ({ openCalendly }), [openCalendly]);

  return (
    <BookingContext.Provider value={value}>
      {children}
      {modal}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const ctx = useContext(BookingContext);
  if (ctx === undefined) {
    throw new Error('useBooking must be used within BookingProvider');
  }
  return ctx;
}
